import { useEffect, useRef, useState } from 'react';
import { type IntakeBatch } from '@project/core';

import { CONVERTIBLE_EXTENSIONS } from './convert';
import { readClipboard, transferredInput, type ClipboardInput } from './clipboard';
import type { SiteMessages } from './i18n';
import type { IntakeReply } from './intake-worker';
import type { IntakeProgress } from './intake';
import { funnelExamples } from './funnel-examples';

const ACCEPT = [
  'image/*',
  '.heic',
  '.heif',
  'text/*',
  '.txt',
  '.md',
  '.csv',
  '.eml',
  '.msg',
  '.docx',
  '.rtf',
  '.html',
  '.htm',
  ...CONVERTIBLE_EXTENSIONS.map((extension) => `.${extension}`),
].join(',');

export function Funnel({
  typed,
  setTyped,
  m,
  language,
  onBatch,
}: {
  readonly typed: string;
  readonly setTyped: React.Dispatch<React.SetStateAction<string>>;
  readonly m: SiteMessages;
  readonly language: string;
  readonly onBatch: (batch: IntakeBatch) => void;
}): React.JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<IntakeProgress>('reading');
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const chooser = useRef<HTMLInputElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const worker = useRef<Worker | null>(null);
  const operation = useRef(0);
  const lastExample = useRef<string | null>(null);
  const timeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const w = m.workflow;

  useEffect(
    () => () => {
      operation.current += 1;
      worker.current?.terminate();
      clearTimeout(timeout.current);
    },
    [],
  );

  // Browsers cannot restore a file chooser or an in-flight worker from session storage.
  useEffect(() => {
    if (!file && !busy) return;
    const warn = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [file, busy]);

  const finish = (): void => {
    operation.current += 1;
    worker.current?.terminate();
    worker.current = null;
    clearTimeout(timeout.current);
    setBusy(false);
  };

  const clearFile = (): void => {
    setFile(null);
    if (chooser.current) chooser.current.value = '';
  };

  const accept = (value: ClipboardInput): void => {
    setProblem(null);
    setNotice('');
    if (value.file) {
      setFile(value.file);
      if (chooser.current) chooser.current.value = '';
    } else if (value.text) {
      setTyped((previous) => (previous ? `${previous}\n${value.text}` : value.text!));
    }
    input.current?.focus();
  };

  const pasteClipboard = async (): Promise<void> => {
    if (busy) return;
    const current = ++operation.current;
    setBusy(true);
    setStage('reading');
    setProblem(null);
    setNotice('');
    try {
      const value = await readClipboard();
      if (current === operation.current) accept(value);
    } catch {
      if (current === operation.current) setProblem(w.clipboardFailed);
    } finally {
      if (current === operation.current) {
        finish();
        input.current?.focus();
      }
    }
  };

  const read = (): void => {
    if (busy) return;
    const current = ++operation.current;
    setBusy(true);
    setStage(file ? 'loading' : 'parsing');
    setProblem(null);
    setNotice('');
    try {
      const reader = new Worker(new URL('./intake-worker.ts', import.meta.url), { type: 'module' });
      worker.current = reader;
      const fail = (): void => {
        if (current !== operation.current) return;
        operation.current += 1;
        setProblem(m.funnelFailed);
        finish();
      };
      reader.onerror = fail;
      timeout.current = setTimeout(fail, 120_000);
      reader.onmessage = (event: MessageEvent<IntakeReply>) => {
        if (current !== operation.current) return;
        const reply = event.data;
        if (reply.kind === 'progress') {
          setStage(reply.stage);
          return;
        }
        if (reply.kind === 'error') {
          fail();
          return;
        }
        const { result } = reply;
        if (result.batch.forms.length > 0) {
          onBatch(result.batch);
          setTyped('');
          if (!result.problem) clearFile();
        }
        setProblem(result.problem ? w[result.problem] : null);
        finish();
      };
      reader.postMessage({ typed, file, language });
    } catch {
      setProblem(m.funnelFailed);
      finish();
    }
  };

  return (
    <div
      className="funnel"
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={(event) => {
        event.preventDefault();
        if (busy) return;
        if (event.dataTransfer.files.length > 1) {
          setProblem(w.help);
          return;
        }
        accept(transferredInput(event.dataTransfer));
      }}
      onPaste={(event) => {
        if (busy) {
          event.preventDefault();
          return;
        }
        if (event.clipboardData.files.length > 1) {
          event.preventDefault();
          setProblem(w.help);
          return;
        }
        const value = transferredInput(event.clipboardData);
        if (value.file) {
          event.preventDefault();
          accept(value);
        }
      }}
    >
      <label className="funnel-label" htmlFor="funnel-input">
        {m.funnelLabel}
      </label>
      <p id="funnel-help" className="funnel-note">
        {w.help}
      </p>
      <textarea
        ref={input}
        id="funnel-input"
        className="funnel-input"
        aria-describedby="funnel-help"
        rows={4}
        readOnly={busy}
        value={typed}
        placeholder={m.funnelPlaceholder}
        onChange={(event) => setTyped(event.target.value)}
      />
      <div className="funnel-actions">
        <button type="button" disabled={busy} onClick={() => void pasteClipboard()}>
          {w.clipboard}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            const examples = funnelExamples(new Date().getFullYear() + 1, m.exampleTitles).filter(
              (example) => example !== lastExample.current,
            );
            const text = examples[Math.floor(Math.random() * examples.length)]!;
            lastExample.current = text;
            accept({ text });
          }}
        >
          {w.example}
        </button>
      </div>
      <div className="funnel-actions">
        <input
          ref={chooser}
          id="funnel-file"
          aria-label={m.funnelLabel}
          className="funnel-file"
          type="file"
          accept={ACCEPT}
          disabled={busy}
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
            setProblem(null);
          }}
        />
        <button
          type="button"
          className="funnel-go"
          disabled={busy || (typed.trim() === '' && file === null)}
          onClick={read}
        >
          {m.funnelAction}
        </button>
        {busy && (
          <button
            type="button"
            onClick={() => {
              operation.current += 1;
              finish();
              setNotice(w.cancelled);
              input.current?.focus();
            }}
          >
            {w.cancel}
          </button>
        )}
      </div>
      {file !== null && (
        <div className="funnel-attachment">
          <span>{file.name}</span>
          <button type="button" disabled={busy} onClick={clearFile}>
            {m.listRemove}
          </button>
          {file.type.startsWith('image/') && <p className="funnel-note">{m.funnelImageNote}</p>}
        </div>
      )}
      <p role="status">{busy ? (stage === 'reading' ? m.funnelBusy : w[stage]) : notice}</p>
      {problem !== null && (
        <p role="alert" className="funnel-problem">
          {problem}
        </p>
      )}
    </div>
  );
}
